import type { Customer, Interaction, AuditLog, SearchFilters } from '../types';

const API_BASE = '/api';

async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  const data = (await response.json()) as T & { error?: string };

  if (!response.ok) {
    throw new Error(data.error || 'An error occurred');
  }

  return data;
}

// Customer API
export async function searchCustomers(
  filters: SearchFilters
): Promise<{ customers: Customer[] }> {
  const params = new URLSearchParams();
  if (filters.email) params.set('email', filters.email);
  if (filters.firstName) params.set('firstName', filters.firstName);
  if (filters.lastName) params.set('lastName', filters.lastName);
  if (filters.externalId) params.set('externalId', filters.externalId);

  return fetchApi(`/customers?${params.toString()}`);
}

export async function getCustomer(
  id: string
): Promise<{ customer: Customer; interactions: Interaction[] }> {
  return fetchApi(`/customers/${id}`);
}

// Actions API
export async function suspendAccount(params: {
  customerId: string;
  customerEmail?: string;
  reason: string;
  duration?: string;
  notes?: string;
}): Promise<{ success: boolean; message: string }> {
  return fetchApi('/actions/suspend', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function closeAccount(params: {
  customerId: string;
  customerEmail?: string;
  reason: string;
  notes?: string;
}): Promise<{ success: boolean; message: string }> {
  return fetchApi('/actions/close', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function addNote(params: {
  customerId: string;
  customerEmail?: string;
  note: string;
  category?: string;
}): Promise<{ success: boolean; message: string }> {
  return fetchApi('/actions/notes', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

// Interactions API
export async function getInteractions(
  customerId: string,
  options?: { limit?: number; offset?: number }
): Promise<{ interactions: Interaction[] }> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.offset) params.set('offset', options.offset.toString());

  const query = params.toString();
  return fetchApi(`/interactions/${customerId}${query ? `?${query}` : ''}`);
}

export async function createInteraction(
  customerId: string,
  params: {
    customerEmail?: string;
    interactionType: string;
    channel?: string;
    summary?: string;
    details?: unknown;
    status?: string;
  }
): Promise<{ success: boolean; id: number }> {
  return fetchApi(`/interactions/${customerId}`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function updateInteraction(
  customerId: string,
  params: {
    interactionId: number;
    status?: string;
    resolution?: string;
    summary?: string;
    details?: unknown;
  }
): Promise<{ success: boolean }> {
  return fetchApi(`/interactions/${customerId}`, {
    method: 'PUT',
    body: JSON.stringify(params),
  });
}

// Audit API
export async function getAuditLogs(options?: {
  customerId?: string;
  agentEmail?: string;
  limit?: number;
  offset?: number;
}): Promise<{ logs: AuditLog[] }> {
  const params = new URLSearchParams();
  if (options?.customerId) params.set('customerId', options.customerId);
  if (options?.agentEmail) params.set('agentEmail', options.agentEmail);
  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.offset) params.set('offset', options.offset.toString());

  const query = params.toString();
  return fetchApi(`/audit${query ? `?${query}` : ''}`);
}
